import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ClipboardList, Activity, Play, BookOpen, Calendar } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { STEPS } from "../data/courseData";

const EXERCISES = [
  { key: "survey", label: "Survey", sub: "Awareness", icon: ClipboardList, path: "survey" },
  { key: "pain_scan", label: "Body Pain Scan", sub: "Map the pain", icon: Activity, path: "pain-scan" },
  { key: "video", label: "Video Lesson", sub: "Reprogramming", icon: Play, path: "video" },
  { key: "journal", label: "Guided Journal", sub: "Neutralization", icon: BookOpen, path: "journal" },
  { key: "daily_practice", label: "Daily Activities", sub: "Reinforce with the Transformer", icon: Calendar, path: "daily-practice" },
];

export default function StepDetail() {
  const { step } = useParams();
  const stepNum = parseInt(step, 10);
  const meta = STEPS.find((s) => s.step === stepNum);
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get("/step-progress").then((r) => setProgress(r.data.find((p) => p.step === stepNum)));
  }, [stepNum]);

  if (!meta) return <Layout><div>Step not found</div></Layout>;
  const done = new Set(progress?.exercises_completed || []);

  return (
    <Layout>
      <button onClick={() => navigate("/journey")} className="text-sm text-[#9AAEC6] flex items-center gap-1 mb-4" data-testid="back-to-journey">
        <ArrowLeft size={16} /> Journey
      </button>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="bps-eyebrow" style={{ color: meta.accent }}>Week {meta.step}</div>
        <h1 className="font-display text-4xl font-light tracking-tighter mt-1">{meta.title}</h1>
        <p className="text-sm text-[#9AAEC6] mt-2">{meta.description}</p>

        <div className="mt-5 bps-glass rounded-3xl p-5">
          <div className="bps-eyebrow mb-1">Psychological shift</div>
          <p className="font-display text-lg italic">"{meta.shift}"</p>
        </div>

        <div className="mt-6 space-y-3" data-testid="exercise-list">
          {EXERCISES.map((e, idx) => {
            const completed = done.has(e.key);
            const Icon = e.icon;
            return (
              <Link
                key={e.key}
                to={`/journey/${stepNum}/${e.path}`}
                data-testid={`exercise-${e.key}`}
                className="block bps-card p-5 hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${completed ? "bg-[#10B981]" : "bg-white/5"}`}>
                    {completed ? <Check size={20} className="text-white" strokeWidth={2.2} /> : <Icon size={20} strokeWidth={1.6} className="text-white" />}
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#7C8FA8]">Practice {idx + 1}</div>
                    <div className="font-display text-lg font-medium tracking-tight">{e.label}</div>
                    <div className="text-xs text-[#9AAEC6]">{e.sub}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {progress?.is_complete && (
          <div className="mt-6 bps-card p-5 border-[#10B981] bg-[#10B981]/15 text-center" data-testid="step-complete-banner">
            <div className="bps-eyebrow text-[#10B981]">Week complete</div>
            <p className="mt-1 text-sm text-white">You've moved through every layer of this week. Beautiful work.</p>
          </div>
        )}
      </motion.div>
    </Layout>
  );
}
